import { useState, useEffect, useCallback } from 'react';
import familyAPIService from '../services/familyAPI';

/**
 * 新設計: Family-groups専用管理フック
 * 旧useWeddingフックを完全に置き換える
 */

/**
 * groupNameから表示用のファミリー名を抽出するヘルパー関数
 * 新旧両方の形式に対応
 */
const extractFamilyNameFromGroupName = (groupName) => {
  if (!groupName) return '';
  
  // 新形式（UUID）の場合：family_12345678-1234-5678-9012-123456789abc
  if (groupName.includes('-')) {
    return ''; // UUIDの場合は抽出不可能なので空文字を返す
  }
  
  // 旧形式の場合：family_南家_1749346223880
  const parts = groupName.split('_');
  if (parts.length >= 3 && parts[0] === 'family') {
    // 最後の部分（タイムスタンプ）を除いて、残りを結合
    const extractedName = parts.slice(1, -1).join('_');
    
    // 日本語の文字化け（URLエンコード）をデコードを試行
    try {
      // %エンコードされている場合
      if (extractedName.includes('%')) {
        return decodeURIComponent(extractedName);
      }
      
      // Base64エンコードされている可能性をチェック
      // 例: 5Y2X5a62 -> 南家
      if (/^[A-Za-z0-9+/=]+$/.test(extractedName) && extractedName.length % 4 === 0) {
        try {
          const decoded = atob(extractedName);
          // UTF-8バイト列をデコード
          const utf8Decoder = new TextDecoder('utf-8');
          const bytes = new Uint8Array(decoded.split('').map(char => char.charCodeAt(0)));
          const decodedText = utf8Decoder.decode(bytes);
          
          // 日本語文字が含まれている場合のみ使用
          if (/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(decodedText)) {
            return decodedText;
          }
        } catch (e) {
          // Base64デコードに失敗した場合は元の文字列を使用
        }
      }
      
      return extractedName;
    } catch (e) {
      // デコードに失敗した場合は元の文字列を返す
      return extractedName;
    }
  }
  
  return '';
};

/**
 * 表示用ファミリー名を取得するヘルパー関数
 * familyNameフィールドを優先し、なければgroupNameから抽出
 */
const getDisplayFamilyName = (familyGroup) => {
  if (!familyGroup) return '';
  
  // familyNameフィールドがあればそれを使用（推奨）
  if (familyGroup.familyName) {
    return familyGroup.familyName;
  }
  
  // フォールバック：groupNameから抽出を試行
  const extractedName = extractFamilyNameFromGroupName(familyGroup.groupName);
  
  // 抽出できた場合はそれを使用、できない場合は「ファミリー」をデフォルトとして使用
  return extractedName || 'ファミリー';
};

/**
 * 既存データのfamilyNameフィールドを自動補完するヘルパー関数
 */
const autoFillFamilyNameForLegacyData = async (familyGroup, updateFamilyName) => {
  // 既にfamilyNameが設定されている場合は何もしない
  if (familyGroup.familyName) {
    return familyGroup.familyName;
  }
  
  // groupNameから名前を抽出
  const extractedName = extractFamilyNameFromGroupName(familyGroup.groupName);
  
  if (extractedName && extractedName !== 'ファミリー') {
    try {
      // 自動的にfamilyNameフィールドを設定
      console.log(`Auto-filling familyName for legacy data: ${extractedName}`);
      await updateFamilyName(extractedName);
      return extractedName;
    } catch (error) {
      console.error('Failed to auto-fill familyName for legacy data:', error);
      return extractedName; // 更新に失敗してもextractedNameは返す
    }
  }
  
  return 'ファミリー';
};

export const useFamily = (user = null) => {
  // ファミリーアカウント状態
  const [familyGroup, setFamilyGroup] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  
  // タスク状態
  const [tasks, setTasks] = useState([]);
  
  // 設定状態
  const [settings, setSettings] = useState({
    weddingDate: '',
    venue: '',
    budget: 0,
    estimatedGuests: 0
  });
  
  // ローディング・エラー状態
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // 初期化完了フラグを追加
  const [error, setError] = useState(null);

  /**
   * エラーハンドリング
   */
  const handleError = useCallback((error, operation) => {
    console.error(`Error in ${operation}:`, error);
    setError(error.message || `${operation}に失敗しました`);
  }, []);

  /**
   * エラークリア
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * ファミリーアカウント取得
   */
  const fetchFamilyGroup = useCallback(async () => {
    if (!user) {
      console.log('🏠 useFamily: fetchFamilyGroup - No user provided, skipping fetch');
      setIsInitialized(true);
      return null;
    }
    
    console.log('🏠 useFamily: fetchFamilyGroup - Starting fetch for user:', {
      username: user.username,
      email: user.email,
      sub: user.sub,
      attributes: user.attributes
    });
    setIsLoading(true);
    setError(null);

    try {
      console.log('🏠 useFamily: fetchFamilyGroup - Calling familyAPIService.getFamilyGroups()...');
      const response = await familyAPIService.getFamilyGroups();
      console.log('🏠 useFamily: fetchFamilyGroup - API response received:', response);

      if (response && response.familyGroups && response.familyGroups.length > 0) {
        const validGroup = response.familyGroups[0];
        console.log('🏠 useFamily: fetchFamilyGroup - Valid group found:', validGroup);
        
        setFamilyGroup(validGroup);
        setMemberCount(validGroup.memberCount || 0);
        
        // isOwnerの判定を修正：メンバー情報から現在のユーザーがオーナーかどうかを判定
        // 複数のユーザー識別子（username、email、sub）で比較
        const currentUserMember = validGroup.members?.find(member => 
          member.userId === user.username || 
          member.email === user.username ||
          member.userId === user.email ||
          member.email === user.email ||
          member.userId === user.sub ||
          member.email === user.sub ||
          (user.attributes && member.email === user.attributes.email) ||
          (user.attributes && member.userId === user.attributes.sub)
        );
        const userIsOwner = currentUserMember?.role === 'owner';
        setIsOwner(userIsOwner);
        console.log('🔍 useFamily: fetchFamilyGroup - isOwner determination DEBUG:', {
          currentUserMember,
          userIsOwner,
          user: {
            username: user.username,
            email: user.email,
            sub: user.sub,
            attributes: user.attributes
          },
          allMembers: validGroup.members?.map(m => ({
            userId: m.userId,
            email: m.email,
            role: m.role,
            displayName: m.displayName
          })),
          matchConditions: {
            'member.userId === user.username': validGroup.members?.map(m => `${m.userId} === ${user.username} = ${m.userId === user.username}`),
            'member.email === user.email': validGroup.members?.map(m => `${m.email} === ${user.email} = ${m.email === user.email}`),
            'member.userId === user.sub': validGroup.members?.map(m => `${m.userId} === ${user.sub} = ${m.userId === user.sub}`)
          }
        });
        
        // familySettingsをsettingsに変換
        if (validGroup.familySettings) {
          setSettings({
            weddingDate: validGroup.familySettings.weddingDate || '',
            venue: validGroup.familySettings.venue || '',
            budget: validGroup.familySettings.budget || 0,
            estimatedGuests: validGroup.familySettings.estimatedGuests || 0
          });
          console.log('fetchFamilyGroup: Settings updated from familySettings:', validGroup.familySettings);
        }
        
        // 既存データの自動補完（非同期で実行、エラーは無視）
        // 注意：updateFamilyNameとの循環依存を避けるため、この処理は無効化
        /*
        if (!validGroup.familyName) {
          autoFillFamilyNameForLegacyData(validGroup, updateFamilyName).catch(error => {
            console.warn('Auto-fill familyName failed (non-critical):', error);
          });
        }
        */
        
        // 初期化完了をマーク
        setIsInitialized(true);
        
        return validGroup;
      } else {
        console.log('fetchFamilyGroup: No valid family groups found - setting familyGroup to null');
        console.log('fetchFamilyGroup: Condition check - response:', !!response);
        console.log('fetchFamilyGroup: Condition check - response.familyGroups:', !!response?.familyGroups);
        console.log('fetchFamilyGroup: Condition check - length > 0:', response?.familyGroups?.length > 0);
        
        setFamilyGroup(null);
        setMemberCount(0);
        setIsOwner(false);
        
        // 早期解除
        setIsLoading(false);
        
        // ファミリーアカウントが見つからない場合も初期化完了
        setIsInitialized(true);
        
        return null;
      }
    } catch (error) {
      console.error('fetchFamilyGroup: Failed with error:', error);
      console.error('fetchFamilyGroup: Error type:', typeof error);
      console.error('fetchFamilyGroup: Error message:', error.message);
      console.error('fetchFamilyGroup: Error stack:', error.stack);
      
      handleError(error, 'ファミリーアカウント取得');
      
      // **エラー時も初期化完了をマーク**
      setIsInitialized(true);
      
      return null;
    } finally {
      // 万が一のためのフォールバック
      console.log('fetchFamilyGroup: Final isLoading cleanup');
      setIsLoading(false);
      
      // **最終的に初期化完了を保証**
      setIsInitialized(true);
    }
  }, [user, handleError]); // 依存関係を明示的に追加

  /**
   * ファミリーアカウント作成
   */
  const createFamilyGroup = useCallback(async (familyName, description = '') => {
    if (!user) return null;

    setIsLoading(true);
    setError(null);

    try {
      const response = await familyAPIService.createFamilyGroup(familyName, description);
      
      if (response.familyGroup || response.groupName) {
        // 作成後にグループ情報を再取得
        await fetchFamilyGroup();
        return response;
      }
    } catch (error) {
      handleError(error, 'ファミリーアカウント作成');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, handleError, fetchFamilyGroup]); // 依存関係を明示的に追加

  /**
   * タスク一覧取得（familyGroupデータを直接指定）
   */
  const fetchTasksWithGroup = useCallback(async (targetFamilyGroup, filters = {}) => {
    if (!user) {
      console.log('fetchTasksWithGroup: No user');
      return;
    }

    // 指定されたファミリーアカウントがない場合は即座に終了
    if (!targetFamilyGroup) {
      console.log('fetchTasksWithGroup: No target family group provided, skipping task fetch');
      return;
    }
    
    console.log('fetchTasksWithGroup: Starting task fetch for group:', targetFamilyGroup.groupName);
    setIsLoading(true);
    setError(null);

    try {
      // ファミリーグループのタスクを取得
      const response = await familyAPIService.getFamilyTasks(targetFamilyGroup.groupName);
      console.log('fetchTasksWithGroup: Raw API response:', response);
      
      // レスポンスの形式を確認して適切にタスクを抽出
      let rawTasks = [];
      if (Array.isArray(response)) {
        // responseが配列の場合
        rawTasks = response;
      } else if (response && Array.isArray(response.tasks)) {
        // responseがオブジェクトでtasksプロパティがある場合
        rawTasks = response.tasks;
      } else if (response && Array.isArray(response)) {
        // responseがオブジェクトだが直接配列の場合
        rawTasks = response;
      } else {
        console.warn('fetchTasksWithGroup: Unexpected response format:', response);
        rawTasks = [];
      }
      
      console.log('fetchTasksWithGroup: Extracted raw tasks:', rawTasks);
      
      // DynamoDBのアイテム形式からタスクデータを抽出・正規化
      const normalizedTasks = rawTasks.map(item => {
        // DynamoDBのアイテム形式の場合
        if (item.Data && typeof item.Data === 'object') {
          return {
            ...item.Data,
            createdAt: item.CreatedAt,
            updatedAt: item.UpdatedAt,
            createdBy: item.CreatedBy
          };
        }
        // 既に正規化されている場合
        return item;
      });
      
      console.log('fetchTasksWithGroup: Normalized tasks:', normalizedTasks);
      setTasks(normalizedTasks);
      
    } catch (error) {
      console.error('fetchTasksWithGroup: Failed:', error);
      handleError(error, 'タスク取得');
    } finally {
      console.log('fetchTasksWithGroup: Setting isLoading to false');
      setIsLoading(false);
    }
  }, [user, handleError]);

  /**
   * タスク一覧取得
   */
  const fetchTasks = useCallback(async (filters = {}) => {
    if (!user) {
      console.log('fetchTasks: No user');
      return;
    }
    
    // ファミリーアカウントがない場合は即座に終了
    if (!familyGroup) {
      console.log('fetchTasks: No family group, skipping task fetch');
      return;
    }
    
    console.log('fetchTasks: Starting task fetch for group:', familyGroup.groupName);
    setIsLoading(true);
    setError(null);

    try {
      // ファミリーグループのタスクを取得
      const response = await familyAPIService.getFamilyTasks(familyGroup.groupName);
      console.log('fetchTasks: Raw API response:', response);
      
      // レスポンスの形式を確認して適切にタスクを抽出
      let rawTasks = [];
      if (Array.isArray(response)) {
        // responseが配列の場合
        rawTasks = response;
      } else if (response && Array.isArray(response.tasks)) {
        // responseがオブジェクトでtasksプロパティがある場合
        rawTasks = response.tasks;
      } else if (response && Array.isArray(response)) {
        // responseがオブジェクトだが直接配列の場合
        rawTasks = response;
      } else {
        console.warn('fetchTasks: Unexpected response format:', response);
        rawTasks = [];
      }
      
      console.log('fetchTasks: Extracted raw tasks:', rawTasks);
      
      // DynamoDBのアイテム形式からタスクデータを抽出・正規化
      const normalizedTasks = rawTasks.map(item => {
        // DynamoDBのアイテム形式の場合
        if (item.Data && typeof item.Data === 'object') {
          return {
            ...item.Data,
            createdAt: item.CreatedAt,
            updatedAt: item.UpdatedAt,
            createdBy: item.CreatedBy
          };
        }
        // 既に正規化されている場合
        return item;
      });
      
      console.log('fetchTasks: Normalized tasks:', normalizedTasks);
      setTasks(normalizedTasks);
      
    } catch (error) {
      console.error('fetchTasks: Failed:', error);
      handleError(error, 'タスク取得');
    } finally {
      console.log('fetchTasks: Setting isLoading to false');
      setIsLoading(false);
    }
  }, [familyGroup, handleError, user]);

  /**
   * ファミリー名更新
   */
  const updateFamilyName = useCallback(async (newName) => {
    if (!familyGroup || !user) return;

    setIsLoading(true);
    const originalGroup = { ...familyGroup };

    try {
      setFamilyGroup(g => (g ? { ...g, familyName: newName } : null));
      await familyAPIService.updateFamilyGroup(familyGroup.groupName, { familyName: newName });
    } catch (error) {
      handleError(error, 'ファミリー名更新');
      setFamilyGroup(originalGroup);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError]);

  /**
   * 設定更新
   */
  const updateSettings = useCallback(async (newSettings) => {
    if (!user || !familyGroup) return false;

    setIsLoading(true);
    setError(null);

    const originalSettings = { ...settings }; // ロールバック用に元の状態を保存

    try {
      const response = await familyAPIService.updateFamilySettings(familyGroup.groupName, newSettings);
      const updatedSettings = response.familySettings || newSettings;
      setSettings(updatedSettings);
      
      // ファミリーアカウントの設定も更新
      setFamilyGroup(prev => ({
        ...prev,
        familySettings: updatedSettings
      }));
      
      return true;
    } catch (error) {
      handleError(error, '設定更新');
      // 状態を元に戻す
      setSettings(originalSettings);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError, settings]);

  /**
   * メンバーの役割更新
   */
  const updateMemberRole = useCallback(async (memberId, newRole) => {
    if (!familyGroup || !user) return;

    setIsLoading(true);
    const originalGroup = { ...familyGroup }; // ロールバック用に元の状態を保存

    try {
      // 楽観的更新
      setFamilyGroup(g => (g ? {
        ...g,
        members: g.members.map(m => m.id === memberId ? { ...m, role: newRole } : m)
      } : null));
      
      await familyAPIService.updateMemberRole(familyGroup.groupName, memberId, newRole);
      
      // 成功時は何もしない（すでに楽観的更新済み）

    } catch (error) {
      handleError(error, '役割更新');
      // エラー時に状態を元に戻す
      setFamilyGroup(originalGroup);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError]);

  /**
   * メンバー削除
   */
  const removeMember = useCallback(async (memberId) => {
    if (!familyGroup || !user) return;

    setIsLoading(true);
    const originalGroup = { ...familyGroup }; // ロールバック用に元の状態を保存

    try {
      // 楽観的更新
      setFamilyGroup(g => (g ? {
        ...g,
        members: g.members.filter(m => m.id !== memberId)
      } : null));
      
      await familyAPIService.removeMember(familyGroup.groupName, memberId);
      
      // 成功時は何もしない（すでに楽観的更新済み）

    } catch (error) {
      handleError(error, 'メンバー削除');
      // エラー時に状態を元に戻す
      setFamilyGroup(originalGroup);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError]);

  /**
   * ファミリーから脱退
   */
  const leaveFamily = useCallback(async () => {
    if (!familyGroup || !user) return;

    setIsLoading(true);
    const originalGroup = { ...familyGroup }; // ロールバック用に元の状態を保存

    try {
      // 楽観的更新
      setFamilyGroup(g => (g ? {
        ...g,
        members: g.members.filter(m => m.userId !== user.username)
      } : null));
      
      await familyAPIService.leaveFamily(familyGroup.groupName);
      
      // 成功時は何もしない（すでに楽観的更新済み）

    } catch (error) {
      handleError(error, 'ファミリー脱退');
      // エラー時に状態を元に戻す
      setFamilyGroup(originalGroup);
      throw error; // エラーを呼び出し元に伝える
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError]);

  /**
   * ファミリーアカウント削除
   */
  const deleteFamily = useCallback(async () => {
    if (!familyGroup || !user) return;

    setIsLoading(true);
    const originalGroup = { ...familyGroup }; // ロールバック用に元の状態を保存

    try {
      // 楽観的更新
      setFamilyGroup(null);
      
      await familyAPIService.deleteFamily(familyGroup.groupName);
      
      // 成功時は何もしない（すでに楽観的更新済み）

    } catch (error) {
      handleError(error, 'ファミリーアカウント削除');
      // エラー時に状態を元に戻す
      setFamilyGroup(originalGroup);
      throw error; // エラーを呼び出し元に伝える
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError]);

  /**
   * タスク追加
   */
  const addTask = useCallback(async (taskData) => {
    if (!familyGroup || !user) return null;

    setIsLoading(true);
    setError(null);

    try {
      const response = await familyAPIService.createFamilyTask(familyGroup.groupName, taskData);
      console.log('addTask: API response:', response);
      
      // レスポンスからタスクデータを抽出
      let newTask;
      if (response && response.task) {
        newTask = response.task;
      } else if (response) {
        // レスポンス全体がタスクデータの場合
        newTask = response;
      } else {
        console.error('addTask: Unexpected response format:', response);
        return null;
      }
      
      // タスクリストを更新
      setTasks(prevTasks => [...prevTasks, newTask]);
      
      return newTask;
    } catch (error) {
      handleError(error, 'タスク追加');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError]);

  /**
   * タスク更新
   */
  const updateTask = useCallback(async (taskId, updates) => {
    if (!familyGroup || !user) return null;

    const originalTasks = [...tasks];
    setIsLoading(true);

    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.taskId === taskId
          ? { ...task, ...updates }
          : task
      )
    );

    try {
      const response = await familyAPIService.updateFamilyTask(familyGroup.groupName, taskId, updates);
      const updatedTaskAttributes = response.Attributes;

      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.taskId === taskId
            ? { ...task, ...updatedTaskAttributes }
            : task
        )
      );
      
      return response;
    } catch (error) {
      handleError(error, 'タスク更新');
      setTasks(originalTasks);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError, tasks]);

  /**
   * タスク削除
   */
  const deleteTask = useCallback(async (taskId) => {
    if (!familyGroup || !user) return false;

    const originalTasks = [...tasks];
    setIsLoading(true);

    try {
      setTasks(prevTasks => prevTasks.filter(t => t.taskId !== taskId));
      await familyAPIService.deleteFamilyTask(familyGroup.groupName, taskId);
      return true;
    } catch (error) {
      handleError(error, 'タスク削除');
      setTasks(originalTasks);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [familyGroup, user, handleError, tasks]);

  /**
   * タスクの完了状態を切り替え
   */
  const toggleTaskCompletion = useCallback(async (task) => {
    if (!task || !task.taskId) return;
    
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    const updates = { status: newStatus };

    await updateTask(task.taskId, updates);
  }, [updateTask]);

  /**
   * 自分にタスクを割り当て
   */
  const assignTaskToSelf = useCallback(async (taskId) => {
    if (!user) return;
    const updates = { assignedTo: user.username };
    await updateTask(taskId, updates);
  }, [user, updateTask]);

  /**
   * タスクの割り当てを解除
   */
  const unassignTask = useCallback(async (taskId) => {
    const updates = { assignedTo: null };
    await updateTask(taskId, updates);
  }, [updateTask]);

  /**
   * 招待リンク生成
   */
  const generateInviteLink = useCallback(async () => {
    if (!user || !familyGroup) return null;

    setIsLoading(true);
    setError(null);

    try {
      const response = await familyAPIService.generateInviteToken(familyGroup.groupName);
      
      // ファミリーアカウントの招待情報を更新
      setFamilyGroup(prev => ({
        ...prev,
        inviteToken: response.inviteToken,
        inviteLink: response.inviteLink
      }));
      
      return response;
    } catch (error) {
      handleError(error, '招待リンク生成');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, familyGroup, handleError]); // 依存関係を明示的に追加

  /**
   * 招待による参加
   */
  const joinByInviteToken = useCallback(async (inviteToken) => {
    if (!user) {
      throw new Error('ユーザーが認証されていません');
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('joinByInviteToken: Starting join process for token:', inviteToken);
      const response = await familyAPIService.joinByInviteToken(inviteToken);
      console.log('joinByInviteToken: API response:', response);
      
      // 参加後にファミリーアカウント情報を再取得
      console.log('joinByInviteToken: Refreshing family group data...');
      await fetchFamilyGroup();
      
      // レスポンスの形式を統一
      if (response && response.weddingGroup) {
        return response;
      } else if (response && response.message) {
        // 成功メッセージがある場合は、新しくファミリーアカウントを取得して返す
        const familyResponse = await familyAPIService.getFamilyGroups();
        if (familyResponse.familyGroups && familyResponse.familyGroups.length > 0) {
          const weddingGroup = familyAPIService.convertFamilyGroupToWeddingGroup(familyResponse.familyGroups[0]);
          return { weddingGroup };
        }
      }
      
      return response;
    } catch (error) {
      console.error('joinByInviteToken: Error details:', error);
      
      // エラーの詳細分析とユーザーフレンドリーなメッセージの提供
      let userMessage = '';
      if (error.response) {
        // HTTP エラーレスポンス
        const statusCode = error.response.status;
        const errorData = error.response.data;
        
        console.error('joinByInviteToken: HTTP error:', statusCode, errorData);
        
        switch (statusCode) {
          case 400:
            if (errorData?.error?.includes('already a member') || errorData?.message?.includes('already a member')) {
              userMessage = '既にこのファミリーアカウントのメンバーです';
            } else if (errorData?.error?.includes('invalid token') || errorData?.message?.includes('invalid token')) {
              userMessage = '招待リンクが無効です。リンクの有効期限が切れているか、正しくない可能性があります';
            } else {
              userMessage = errorData?.error || errorData?.message || '招待リンクが無効です';
            }
            break;
          case 404:
            userMessage = '招待されたファミリーアカウントが見つかりません';
            break;
          case 403:
            userMessage = '招待リンクへのアクセス権限がありません';
            break;
          case 500:
            userMessage = 'サーバーエラーが発生しました。しばらく時間をおいて再度お試しください';
            break;
          default:
            userMessage = `参加処理中にエラーが発生しました (エラーコード: ${statusCode})`;
        }
      } else if (error.message) {
        userMessage = error.message;
      } else {
        userMessage = '招待による参加処理中に予期しないエラーが発生しました';
      }
      
      const enhancedError = new Error(userMessage);
      enhancedError.originalError = error;
      
      handleError(enhancedError, '招待による参加');
      throw enhancedError;
    } finally {
      setIsLoading(false);
    }
  }, [user, handleError, fetchFamilyGroup]); // fetchFamilyGroupを依存配列に追加

  /**
   * メンバー一覧取得
   */
  const fetchMembers = useCallback(async () => {
    if (!user || !familyGroup) return [];

    try {
      return familyGroup.members || [];
    } catch (error) {
      handleError(error, 'メンバー取得');
      return [];
    }
  }, [user, familyGroup, handleError]); // 依存関係を明示的に追加

  /**
   * 初回ロード - 認証確認後の高速初期化プロセス
   */
  useEffect(() => {
    // 認証状態が確定してからのみ初期化開始
    if (user && user.username && !isInitialized) {
      console.log('useFamily: User authenticated, starting fast initialization:', user.username);
      
      // 非同期で即座に開始
      const initializeData = async () => {
        try {
          const familyGroupData = await fetchFamilyGroup();
          
          // ファミリーアカウントが取得できた場合、即座にタスクも並列取得
          if (familyGroupData) {
            console.log('useFamily: Family group loaded, starting parallel task fetch');
            // タスク取得を非同期で開始（familyGroupDataを直接使用）
            fetchTasksWithGroup(familyGroupData).catch(error => {
              console.error('useFamily: Task fetch failed, but continuing:', error);
            });
          }
        } catch (error) {
          console.error('useFamily: Initialization failed:', error);
        }
      };
      
      initializeData();
    } else if (user === null) {
      // ユーザーが明示的にnullの場合（認証失敗）
      console.log('useFamily: User explicitly null, skipping initialization');
    } else {
      // ユーザーが未定義の場合（認証待機中）
      console.log('useFamily: Waiting for user authentication...');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // デバッグ情報をコンソールに出力（簡素化）
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('useFamily Debug:', {
        user: user?.username,
        isInitialized,
        isLoading,
        error: error,
        familyGroup: familyGroup ? {
          groupName: familyGroup.groupName,
          familyName: familyGroup.familyName,
          owner: familyGroup.owner,
          isOwner,
          memberCount,
          members: familyGroup.members?.length,
        } : null,
        tasksCount: tasks.length,
        settings,
      });
    }
  }, [user, isInitialized, isLoading, error, familyGroup, isOwner, memberCount, tasks, settings]);

  /**
   * API接続確認
   */
  const checkApiHealth = useCallback(async () => {
    try {
      return await familyAPIService.healthCheck();
    } catch (error) {
      console.error('Family API health check failed:', error);
      return false;
    }
  }, []);

  /**
   * Family ID可用性チェック
   */
  const checkFamilyIdAvailability = useCallback(async (familyId) => {
    try {
      return await familyAPIService.checkFamilyIdAvailability(familyId);
    } catch (error) {
      console.error('Family ID availability check failed:', error);
      return false;
    }
  }, []);

  // 便利な状態計算
  const isSoloMode = memberCount === 1;
  const isCoupleMode = memberCount === 2;
  const hasFamilyGroup = !!familyGroup;
  const canInvite = hasFamilyGroup && isOwner && isSoloMode;

  // ===========================================
  // 互換性プロパティ（段階的移行用）
  // ===========================================
  
  // WeddingContextとの互換性のため、familyGroupをweddingGroupとしても公開
  const weddingGroup = familyGroup ? {
    id: familyGroup.groupName,
    familyName: familyGroup.familyName || familyGroup.groupName,
    groupName: familyGroup.groupName,
    settings: familyGroup.familySettings || settings,
    members: familyGroup.members || [],
    inviteToken: familyGroup.inviteToken,
    inviteLink: familyGroup.inviteLink,
    createdAt: familyGroup.createdDate,
    description: familyGroup.description
  } : null;

  // 互換性メソッド
  const createWeddingGroup = useCallback(async (familyId, familyName = '') => {
    return await createFamilyGroup(familyName || familyId, `${familyName || familyId}の結婚準備グループ`);
  }, [createFamilyGroup]);

  const createSimpleWeddingGroup = useCallback(async (familyId, familyName = '') => {
    return await createWeddingGroup(familyId, familyName);
  }, [createWeddingGroup]);

  return {
    // 新設計（Familyベース）
    familyGroup,
    hasFamilyGroup,
    createFamilyGroup,
    fetchFamilyGroup,
    
    // 共通プロパティ
    isOwner,
    memberCount,
    isSoloMode,
    isCoupleMode,
    canInvite,
    
    // データ
    tasks,
    settings,
    isLoading,
    isInitialized,
    error,
    
    // タスク操作
    fetchTasks,
    addTask,
    updateTask,
    deleteTask,
    toggleTaskCompletion,
    assignTaskToSelf,
    unassignTask,
    
    // 設定操作
    updateSettings,
    updateFamilyName,
    
    // 招待・参加操作
    generateInviteLink,
    joinByInviteToken,
    fetchMembers,
    
    // ユーティリティ
    clearError,
    checkApiHealth,
    checkFamilyIdAvailability,
    
    // 状態確認
    isAuthenticated: !!user,
    user,
    
    // 互換性プロパティ（段階的移行用）
    weddingGroup,
    hasWeddingGroup: hasFamilyGroup,
    createWeddingGroup,
    createSimpleWeddingGroup
  };
}; 

// ヘルパー関数をエクスポート
export { extractFamilyNameFromGroupName, getDisplayFamilyName }; 